import _ from "lodash";
import { createStore } from "vuex";

import { ApiService } from "./ApiService";
import { Storage } from "./storage";

import { BOOKING_RESET, BOOKING_UPDATE, DISPLAY_UPDATE, SET_ACTS_ID, SET_KEY } from "./mutations.type";

import {
    CREATE_ORDER,
    FETCH_ONGOING,
    FETCH_ORGANISATION,
    FETCH_PLOTS,
    FETCH_POS,
    FETCH_PRODUCT,
    FETCH_PRODUCTS,
    FETCH_RECEIPT,
    SET_LANGUAGE
} from "./actions.type";
import { debit } from "./acts";

const queryParams = new URLSearchParams(window.location.search);
const identifierFromQuery = queryParams.get("identifier");

if (identifierFromQuery) {
    const cleaned = identifierFromQuery.replace(/[\s-]+/g, "");

    if (Storage.setIdentifier(cleaned)) {
        const url = new URL(window.location.href);
        url.searchParams.delete("identifier");
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    }
}

const identifier = Storage.getIdentifier();
const storedLanguage = Storage.getLanguage();

const getLanguageCode = (language) => {
    const normalizeLanguageCode = (value) => {
        if (typeof value !== "string") {
            return null;
        }

        const normalized = value.trim().toLowerCase();

        if (!normalized) {
            return null;
        }

        if (normalized === "ja-jp") {
            return "ja";
        }

        return normalized;
    };

    if (typeof language === "string") {
        return normalizeLanguageCode(language);
    }

    if (!language || typeof language !== "object") {
        return null;
    }

    return [
        language.code,
        language.language,
        language.locale,
        language.key
    ]
        .map((value) => normalizeLanguageCode(value))
        .find(Boolean) || null;
};

const getSupportedLanguages = (supportedLanguages) => (
    Array.isArray(supportedLanguages) ? supportedLanguages : []
);

const isLanguageSupported = (supportedLanguages, language) => {
    if (!language) {
        return false;
    }

    return getSupportedLanguages(supportedLanguages).some((entry) => getLanguageCode(entry) === language);
};

const getLanguageRequestConfig = (state, config = {}) => {
    const activeLanguage = state.language
        || getLanguageCode(state.pos.current_language)
        || getLanguageCode(state.pos.language);

    return ApiService.mergeLanguageConfig(config, activeLanguage);
};

const normalizeProduct = (product, overrides = {}) => ({
    ...(product || {}),
    upsells: Array.isArray(product?.upsells) ? product.upsells : [],
    ...overrides
});

export default createStore({
    state: {
        organisation: null,
        language: storedLanguage,
        pos: {
            online: true,
            identifier: identifier,
            configuration: {
                demo: false,
            },
            acts: false,
        },
        display: {
            cart: false,
            checkout: false,
        },
        products: [],
        ongoing: [],
        plots: [],
        booking: {
            product: {
                id: null,
                plot: null,
                quantity: null,
            },
            audiences: [],
            upsells: [],
            kiosk: true,
        },
        order: null,
        receipt: null,
    },
    getters: {
        getOrganisation(state) {
            return state.organisation;
        },
        getPOS(state) {
            return state.pos;
        },
        getLanguage(state) {
            return state.language;
        },
        getDisplay(state) {
            return state.display;
        },
        getProducts(state) {
            return state.products;
        },
        getProduct: (state) => (id) => {
            const index = _.findIndex(state.products, (product) => product.id === id);
            return state.products[index];
        },
        getOngoing(state) {
            return state.ongoing;
        },
        getPlots(state) {
            return state.plots;
        },
        getPlot: (state) => (id) => {
            const index = _.findIndex(state.plots, (plot) => plot.id === id);
            return state.plots[index];
        },
        getAudience: (state, getters) => (id) => {
            if (state.booking.product.id === null) {
                return null;
            }

            const product = getters.getProduct(state.booking.product.id);
            if (!product || !Array.isArray(product.audiences)) {
                return null;
            }

            const index = _.findIndex(product.audiences, (audience) => audience.id === id);
            return product.audiences[index] ?? null;
        },
        getQuantity(state, getters) {
            let quantity = 0;
            if (state.booking.product.id !== null) {
                const product = getters.getProduct(state.booking.product.id);
                if (product.options?.audiences) {
                    if (state.booking.audiences.length > 0) {
                        quantity = state.booking.audiences
                            .map((audience) => audience.quantity)
                            .reduce((a, b) => a + b, 0);
                    }
                } else {
                    quantity = state.booking.product.quantity;
                }
            }
            return quantity;
        },
        getBooking(state) {
            return state.booking;
        },
        getReceipt(state) {
            return state.receipt;
        },
        getActs(state) {
            return state.pos.acts;
        },
    },
    actions: {
        async [FETCH_ORGANISATION]({ state }) {
            const res = await ApiService.instance.get("/organisation");
            const organisation = res.data.response;
            state.organisation = organisation;
            return organisation;
        },
        async [SET_LANGUAGE]({ state }, language) {
            const selectedLanguage = Storage.setLanguage(language);

            state.language = selectedLanguage;
            ApiService.setLanguage(selectedLanguage);

            return selectedLanguage;
        },
        async [FETCH_POS]({ state }) {
            try {
                const res = await ApiService.instance.get(
                    `/pos/${identifier}`,
                    getLanguageRequestConfig(state)
                );
                const pos = res.data.response;
                const demoFromApi = typeof pos.demoMode === "boolean" ? pos.demoMode : state.pos.configuration.demo;
                const supportedLanguages = getSupportedLanguages(pos.supported_languages);
                const defaultLanguage = getLanguageCode(pos.language);
                const resolvedLanguage = getLanguageCode(pos.current_language);
                const selectedLanguage = isLanguageSupported(supportedLanguages, resolvedLanguage)
                    ? resolvedLanguage
                    : (isLanguageSupported(supportedLanguages, state.language) ? state.language : defaultLanguage);

                pos.online = true;
                pos.configuration = {
                    ...state.pos.configuration,
                    demo: demoFromApi,
                };
                state.pos = pos;
                state.language = Storage.setLanguage(selectedLanguage || defaultLanguage);
                ApiService.setLanguage(state.language);
                return state.pos;
            } catch (error) {
                if (error.response?.data.error === "POS_NOT_FOUND") {
                    state.pos = {
                        online: false,
                        configuration: state.pos.configuration,
                        identifier,
                    };
                    return state.pos;
                }

                throw error;
            }
        },
        async [FETCH_PRODUCTS]({ state }) {
            const productsResponse = await ApiService.instance.get(
                "/products",
                getLanguageRequestConfig(state)
            );
            const whitelist = state.pos.products;
            const products = productsResponse.data.response.filter(
                (product) => whitelist == null || (product && whitelist.includes(product.id))
            );
            state.products = products.map((product) => normalizeProduct(product, { full: false }));

            return state.products;
        },
        async [FETCH_PRODUCT]({ state }, productId) {
            const res = await ApiService.instance.get(
                `/products/${productId}`,
                getLanguageRequestConfig(state)
            );
            state.products = state.products.map((product) => {
                if (product.id === productId) {
                    return normalizeProduct(res.data.response, { full: true });
                }

                return normalizeProduct(product);
            });

            const product = state.products.find((entry) => entry.id === productId);

            if (product) {
                return product;
            }

            const detailedProduct = normalizeProduct(res.data.response, { full: true });
            state.products.push(detailedProduct);
            return detailedProduct;
        },
        async [FETCH_ONGOING]({ state }) {
            const res = await ApiService.instance.get(
                "/products/ongoing",
                getLanguageRequestConfig(state)
            );
            const whitelist = state.pos.products;
            state.ongoing = res.data.response.filter(
                (product) => whitelist == null || whitelist.includes(product.product)
            );
            return state.ongoing;
        },
        async [FETCH_PLOTS]({ state }) {
            const res = await ApiService.instance.get(
                `/products/${state.booking.product.id}/plots`,
                getLanguageRequestConfig(state)
            );
            state.plots = res.data.response;
            return state.plots;
        },
        async [FETCH_RECEIPT]({ state }) {
            try {
                const res = await ApiService.instance.get(
                    `/pos/${state.pos.identifier}/receipt/${state.order.order.hash}`,
                    getLanguageRequestConfig(state)
                );
                state.receipt = res.data.response;
            } catch (error) {
                return error;
            }
        },
        async [CREATE_ORDER]({ state }) {
            const booking = { ...state.booking };
            ApiService.addHeader("Pos-Identifier", state.pos.identifier);
            if (!booking.product.plot) delete booking.product.plot;
            try {
                const res = await ApiService.instance.post(
                    "/orders",
                    booking,
                    getLanguageRequestConfig(state)
                );
                const order = res.data.response;
                state.order = order;
                if (state.pos.acts) {
                    const amountInCents = Math.round(Number(order.order.amount) * 100);
                    state.order.actsId = await debit(amountInCents);
                }
                return order;
            } catch (error) {
                return error.response?.data;
            }
        },
    },
    mutations: {
        [SET_KEY](state, key) {
            state.key = key;
        },
        [SET_ACTS_ID](state, id) {
            state.order.actsId = id;
        },
        [DISPLAY_UPDATE](state, display) {
            state.display[display.component] = display.view;
        },
        [BOOKING_UPDATE](state, booking) {
            state.booking = booking;
        },
        [BOOKING_RESET](state) {
            state.booking = {
                product: {
                    id: null,
                    plot: null,
                    quantity: null,
                },
                audiences: [],
                upsells: [],
                kiosk: true,
            };
        },
    },
});
